import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Grid3X3, Search, Eye } from 'lucide-react';
import './NativeItems.css';

// Bin Lookup (Native) — read-only. Scan or type a bin code, see its contents
// from the synced per-bin balances. A code can exist at more than one location,
// so results are grouped per bin. Deep-linkable at /native-items/bin/:binCode
// (the Bins tab of an item record links straight here).

const fmtQty = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 }));

export default function NativeBinLookup() {
  const { binCode: paramCode } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(paramCode || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const fetchBin = useCallback(async (raw) => {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const { data } = await axios.get(`/api/native-items/bin/${encodeURIComponent(trimmed)}`);
      setResult(data);
    } catch (err) {
      setResult(null);
      setError(
        err.response?.status === 404
          ? `No bin found with code "${trimmed}".`
          : err.response?.data?.error || 'Bin lookup failed'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-run when the route carries a bin code (deep link / links from Bins tab).
  useEffect(() => {
    if (paramCode) {
      setCode(paramCode);
      fetchBin(paramCode);
    }
  }, [paramCode, fetchBin]);

  const submit = (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    // Keep the URL shareable; the effect above does the fetch.
    if (trimmed !== paramCode) navigate(`/native-items/bin/${encodeURIComponent(trimmed)}`);
    else fetchBin(trimmed);
  };

  return (
    <div className="nit-page">
      <div className="nit-header">
        <h1><Grid3X3 size={22} /> Bin Lookup (Native)</h1>
        <p className="nit-sub">
          Scan or type a bin code to see its contents from the synced balances.
          <span className="nit-readonly"><Eye size={12} /> read-only</span>
        </p>
      </div>

      <div className="nit-card">
        <form className="nit-search-row" onSubmit={submit}>
          <div className="nit-search">
            <Search size={16} className="nit-search-icon" />
            <input
              className="nit-input nit-input-search"
              placeholder="Scan or type a bin code (e.g. A-01-03)…"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </div>
          <button type="submit" className="nit-btn nit-btn-primary" disabled={loading || !code.trim()}>
            {loading ? 'Looking…' : 'Look up bin'}
          </button>
        </form>
      </div>

      {error && <div className="nit-error">{error}</div>}
      {loading && <div className="nit-card"><div className="nit-loading">Loading bin…</div></div>}

      {!loading && result && (result.bins || []).map((bin) => {
        const totalUnits = (bin.contents || []).reduce((s, c) => s + (Number(c.quantity) || 0), 0);
        return (
          <div className="nit-card" key={bin.id}>
            <div className="nit-item-head" style={{ marginBottom: 12 }}>
              <code>{bin.binCode}</code>
              {bin.location && <span className="nit-muted">{bin.location.name}{bin.location.code ? ` (${bin.location.code})` : ''}</span>}
              {bin.zone && <span className="nit-tag">{bin.zone}</span>}
              {bin.binType && <span className="nit-tag">{bin.binType}</span>}
              {!bin.isActive && <span className="nit-tag nit-tag-inactive">inactive</span>}
            </div>
            <div className="nit-stats" style={{ marginBottom: 12 }}>
              <div className="nit-stat"><span>SKUs</span><strong>{(bin.contents || []).length}</strong></div>
              <div className="nit-stat"><span>Total units</span><strong className={totalUnits < 0 ? 'nit-neg' : ''}>{fmtQty(totalUnits)}</strong></div>
            </div>
            {(bin.contents || []).length === 0 ? (
              <div className="nit-empty">This bin is empty.</div>
            ) : (
              <div className="nit-table-wrap">
                <table className="nit-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th className="r">Quantity</th>
                      <th className="r">Reserved</th>
                      <th className="r">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bin.contents.map((c, i) => (
                      <tr key={i}>
                        <td>
                          {c.itemId
                            ? <Link to={`/native-items/${encodeURIComponent(c.sku || c.itemId)}`}><code>{c.sku || '—'}</code></Link>
                            : <code>{c.sku || '—'}</code>}
                        </td>
                        <td>{c.name || '—'}</td>
                        <td className="nit-muted">{c.itemType || '—'}</td>
                        <td className={`r ${Number(c.quantity) < 0 ? 'nit-neg' : ''}`}>{fmtQty(c.quantity)}</td>
                        <td className="r">{fmtQty(c.reserved)}</td>
                        <td className={`r ${Number(c.available) < 0 ? 'nit-neg' : ''}`}>{fmtQty(c.available)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {searched && !loading && !error && result && (result.bins || []).length === 0 && (
        <div className="nit-empty">No bin found with that code.</div>
      )}
    </div>
  );
}
